import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Sparkles, RefreshCw, Lightbulb, ListChecks, BookOpen, ArrowRight, AlertCircle, Download, Loader2 } from "lucide-react";
import { useModuleSummary, type GenerateSummaryInput } from "@/hooks/useModuleSummary";
import { useAIQuota } from "@/hooks/useAIQuota";
import { PaywallDialog } from "./PaywallDialog";
import { toast } from "sonner";

interface ModuleSummaryCardProps {
  moduleId: string;
  moduleTitle: string;
  category: string;
  lessonContent?: string | null;
  videoTitles?: string[];
}

export function ModuleSummaryCard({
  moduleId,
  moduleTitle,
  category,
  lessonContent,
  videoTitles,
}: ModuleSummaryCardProps) {
  const { query, generate } = useModuleSummary(moduleId);
  const { quiz_remaining, quiz_limit, isUnlimited } = useAIQuota();
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const summary = query.data?.summary;
  const updatedAt = query.data?.updated_at;

  const handleGenerate = () => {
    const input: GenerateSummaryInput = {
      moduleId,
      moduleTitle,
      category,
      lessonContent: lessonContent ?? undefined,
      videoTitles,
    };
    generate.mutate(input, {
      onError: (err) => {
        if (err.kind === "quota_exceeded") {
          setPaywallOpen(true);
          return;
        }
        toast.error(err.message || "Não foi possível gerar o resumo.");
      },
    });
  };

  const slugify = (s: string) =>
    s
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "")
      .slice(0, 60) || "modulo";

  const handleDownloadPDF = async () => {
    if (!summary) return;
    setIsDownloading(true);
    try {
      const [{ pdf }, { ModuleSummaryPDFDocument }] = await Promise.all([
        import("@react-pdf/renderer"),
        import("./ModuleSummaryPDFDocument"),
      ]);

      const blob = await pdf(
        <ModuleSummaryPDFDocument
          moduleTitle={moduleTitle}
          category={category}
          summary={summary}
          generatedAt={updatedAt}
        />
      ).toBlob();

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `resumo-${slugify(moduleTitle)}-datahireclub.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success("PDF baixado com sucesso!");
    } catch (e) {
      console.error("Erro ao gerar PDF do resumo:", e);
      toast.error("Não foi possível gerar o PDF. Tente novamente.");
    } finally {
      setIsDownloading(false);
    }
  };

  const remainingLabel = isUnlimited
    ? "Ilimitado"
    : `${Math.max(quiz_remaining, 0)} / ${quiz_limit} hoje`;

  return (
    <>
      <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
        <CardContent className="pt-6 space-y-4">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center">
                <Sparkles className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h3 className="font-bold text-base">Resumo IA do módulo</h3>
                <p className="text-xs text-muted-foreground">
                  TL;DR, pontos-chave, conceitos e próximos passos
                </p>
              </div>
            </div>
            <Badge variant="secondary" className="text-xs">{remainingLabel}</Badge>
          </div>

          {/* Loading inicial da query */}
          {query.isLoading && (
            <div className="space-y-2">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
            </div>
          )}

          {/* Estado vazio */}
          {!query.isLoading && !summary && !generate.isPending && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Gere um resumo personalizado para revisar o módulo em segundos.
              </p>
              <Button onClick={handleGenerate} className="gap-2" disabled={generate.isPending}>
                <Sparkles className="h-4 w-4" />
                Gerar resumo com IA
              </Button>
              {!isUnlimited && quiz_remaining <= 0 && (
                <p className="text-xs text-destructive flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  Cota diária esgotada — faça upgrade para continuar.
                </p>
              )}
            </div>
          )}

          {/* Loading de geração */}
          {generate.isPending && (
            <div className="space-y-3">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-5/6" />
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-16 w-full" />
            </div>
          )}

          {/* Resumo pronto */}
          {summary && !generate.isPending && (
            <div className="space-y-5">
              {/* TL;DR */}
              <section>
                <div className="flex items-center gap-2 mb-1.5">
                  <Lightbulb className="h-4 w-4 text-primary" />
                  <h4 className="font-semibold text-sm">TL;DR</h4>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">{summary.tldr}</p>
              </section>

              {/* Pontos-chave */}
              {summary.key_points?.length > 0 && (
                <section>
                  <div className="flex items-center gap-2 mb-2">
                    <ListChecks className="h-4 w-4 text-primary" />
                    <h4 className="font-semibold text-sm">Pontos-chave</h4>
                  </div>
                  <ul className="space-y-1.5">
                    {summary.key_points.map((p, i) => (
                      <li key={i} className="text-sm text-muted-foreground flex gap-2">
                        <span className="text-primary mt-1">•</span>
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {/* Conceitos */}
              {summary.concepts?.length > 0 && (
                <section>
                  <div className="flex items-center gap-2 mb-2">
                    <BookOpen className="h-4 w-4 text-primary" />
                    <h4 className="font-semibold text-sm">Conceitos importantes</h4>
                  </div>
                  <dl className="space-y-2">
                    {summary.concepts.map((c, i) => (
                      <div key={i} className="text-sm">
                        <dt className="font-medium text-foreground">{c.term}</dt>
                        <dd className="text-muted-foreground">{c.definition}</dd>
                      </div>
                    ))}
                  </dl>
                </section>
              )}

              {/* Próximos passos */}
              {summary.next_steps?.length > 0 && (
                <section>
                  <div className="flex items-center gap-2 mb-2">
                    <ArrowRight className="h-4 w-4 text-primary" />
                    <h4 className="font-semibold text-sm">Próximos passos</h4>
                  </div>
                  <ol className="space-y-1.5">
                    {summary.next_steps.map((s, i) => (
                      <li key={i} className="text-sm text-muted-foreground flex gap-2">
                        <span className="font-semibold text-primary">{i + 1}.</span>
                        <span>{s}</span>
                      </li>
                    ))}
                  </ol>
                </section>
              )}

              <div className="flex items-center justify-between pt-2 border-t">
                <span className="text-xs text-muted-foreground">
                  {updatedAt
                    ? `Gerado em ${new Date(updatedAt).toLocaleDateString("pt-BR", {
                        day: "2-digit",
                        month: "short",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}`
                    : ""}
                </span>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleDownloadPDF}
                    disabled={isDownloading}
                    className="gap-1 h-8"
                    aria-label="Baixar resumo em PDF"
                  >
                    {isDownloading ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Download className="h-3.5 w-3.5" />
                    )}
                    Baixar PDF
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleGenerate}
                    disabled={generate.isPending}
                    className="gap-1 h-8"
                  >
                    <RefreshCw className="h-3.5 w-3.5" />
                    Regerar
                  </Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <PaywallDialog open={paywallOpen} onOpenChange={setPaywallOpen} feature="ai_practice" />
    </>
  );
}
